import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { Toaster as Sonner } from "@pps/ui/components/ui/sonner";
import { Toaster } from "@pps/ui/components/ui/toaster";
import { TooltipProvider } from "@pps/ui/components/ui/tooltip";
import { MutationsProvider } from '@pps/mutations';
import { TournamentsProvider } from '@pps/tournaments-hooks';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@pps/ui/hooks/use-toast';
// M-1 PR-d: AuthProvider vem do AuthBridge — único caminho (sem flag,
// sem fallback legado). Pós cleanup do shim blue-green. useAuth continua
// importado de @/contexts/AuthContext (Context + hook), com o bridge
// injetando valores no mesmo Context.
import { AuthProvider } from '@/contexts/AuthBridge';
import { useAuth } from '@/contexts/AuthContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { ProfessorScopeProvider } from '@/contexts/ProfessorScopeContext';
import { AppLayout } from '@/components/AppLayout';
import { PlanGate } from '@/components/PlanGate';
import { RequirePermission } from '@/components/RequirePermission';
import { usePwaUpdate } from '@/hooks/usePwaUpdate';
import { useActivityHeartbeat } from '@/hooks/useActivityHeartbeat';
import { lazy, Suspense, useEffect } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import { AdminCutoverGate } from '@/components/AdminCutoverGate';
import { handleSSOHandshake } from '@/lib/sso-handler';
import { ImpersonationBannerWrapper } from '@/components/ImpersonationBannerWrapper';

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import('@/pages/Auth'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Leads = lazy(() => import('@/pages/Leads'));
const Opportunities = lazy(() => import('@/pages/Opportunities'));
const TrialLessons = lazy(() => import('@/pages/TrialLessons'));
const Aggregators = lazy(() => import('@/pages/Aggregators'));
const Students = lazy(() => import('@/pages/Students'));
const Courts = lazy(() => import('@/pages/Courts'));
const Teachers = lazy(() => import('@/pages/Teachers'));
const Lessons = lazy(() => import('@/pages/Lessons'));
const Tuition = lazy(() => import('@/pages/Tuition'));
const PaymentMethods = lazy(() => import('@/pages/PaymentMethods'));
const Staff = lazy(() => import('@/pages/Staff'));
const Settings = lazy(() => import('@/pages/Settings'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));
const Agenda = lazy(() => import('@/pages/Agenda'));
const Members = lazy(() => import('@/pages/Members'));
const Tournaments = lazy(() => import('@/pages/Tournaments'));
const Atendimento = lazy(() => import('@/pages/Atendimento'));
const Announcements = lazy(() => import('@/pages/Announcements'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Categories = lazy(() => import('@/pages/Categories'));
const WhatsApp = lazy(() => import('@/pages/WhatsApp'));
const Instagram = lazy(() => import('@/pages/Instagram'));
const Expenses = lazy(() => import('@/pages/Expenses'));
const Products = lazy(() => import('@/pages/Products'));
const Pdv = lazy(() => import('@/pages/Pdv'));
const PdvReports = lazy(() => import('@/pages/PdvReports'));
const FinancialDashboard = lazy(() => import('@/pages/FinancialDashboard'));
const Nfse = lazy(() => import('@/pages/Nfse'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const TournamentDetail = lazy(() => import('@/pages/TournamentDetail'));
const TournamentMatchDetail = lazy(() => import('@/pages/TournamentMatchDetail'));
const TournamentTV = lazy(() => import('@/pages/TournamentTV'));
const ClubPublicSite = lazy(() => import('@/pages/ClubPublicSite'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const OperationalIndicators = lazy(() => import('@/pages/OperationalIndicators'));
const NetworkSettings = lazy(() => import('@/pages/settings/NetworkSettings'));
const PlatformFeesAdmin = lazy(() => import('@/pages/admin/PlatformFeesAdmin'));
const Makeup = lazy(() => import('@/pages/Makeup'));
const TeacherCommissions = lazy(() => import('@/pages/TeacherCommissions'));
const ProfessorOnboarding = lazy(() => import('@/pages/professor/ProfessorOnboarding'));
const ProfessorCadastros = lazy(() => import('@/pages/professor/ProfessorCadastros'));
const ProfessorSignupPublic = lazy(() => import('@/pages/professor/ProfessorSignupPublic'));
const ProfessorFinanceiroAlunos = lazy(() => import('@/pages/professor/ProfessorFinanceiroAlunos'));
const ProfessorTeam = lazy(() => import('@/pages/professor/ProfessorTeam'));

const isNative = typeof window.Capacitor !== 'undefined' && window.Capacitor?.isNativePlatform?.();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,   // 2 min — balances freshness vs refetch overhead
      gcTime: 1000 * 60 * 10,     // 10 min garbage collection
      retry: 1,
      refetchOnWindowFocus: false, // avoid unnecessary refetches on tab switch
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace />;

  return <AppLayout>{children}</AppLayout>;
}

/**
 * ProtectedRouteFullScreen — variante de `ProtectedRoute` que exige auth
 * (consistente com PartidaAoVivo / TournamentMatchDetail no CRM, o owner faz
 * login no monitor de quadra) mas NÃO envolve em `<AppLayout>`. Necessário
 * para o Modo TV (Wave 4): sidebar + header do CRM atrapalhariam a leitura
 * à distância em monitor/projetor.
 */
function ProtectedRouteFullScreen({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace />;

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (session) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PwaUpdateWatcher() {
  usePwaUpdate();
  return null;
}

/**
 * Handles incoming SSO handshakes from the Portal app.
 * - Web: checks window.location on mount
 * - Native Android/iOS: listens for App Links / Universal Links via appUrlOpen
 */
function SSOHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Web: check current URL immediately
    if (window.location.pathname.startsWith('/sso')) {
      handleSSOHandshake(window.location.href).then((ok) => {
        navigate(ok ? '/' : '/auth', { replace: true });
      });
    }

    // Native: listen for App Links (Android) / Universal Links (iOS)
    if (!isNative) return;
    let cleanup: (() => void) | undefined;
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appUrlOpen', ({ url }) => {
        handleSSOHandshake(url).then((ok) => {
          navigate(ok ? '/' : '/auth', { replace: true });
        });
      }).then((listener) => {
        cleanup = () => listener.remove();
      });
    });
    return () => cleanup?.();
  }, [navigate]);

  return null;
}

/**
 * Tempo ativo da equipe para a Saúde dos clubes (admin). Uma instância só,
 * fora das rotas: o heartbeat não pode reiniciar a cada navegação. Sem sessão
 * ou sem clube ativo, fica desligado.
 */
function ActivityHeartbeat() {
  const { session, club } = useAuth();
  useActivityHeartbeat(session ? club?.id ?? null : null);
  return null;
}

const liveMatchHref = (matchId: string, tournamentId?: string | null) =>
  tournamentId ? `/torneios/${tournamentId}/partidas/${matchId}` : null;

/**
 * TournamentsProviderBridge — injeta supabase client + userId no
 * `<TournamentsProvider>` consumido pelos hooks de torneios em
 * `@pps/tournaments-hooks` (Wave 2). Espelha o pattern de apps/public/App.tsx
 * (PR #330). Precisa estar DENTRO de `<AuthProvider>` para acessar `useAuth`.
 *
 * IMPORTANT (Gate 1 Wave 1b lesson, IR-PROC-005): sem este provider, qualquer
 * hook que chame `useTournamentsSupabase()` lança em runtime.
 *
 * `playerHref` é omitido DE PROPÓSITO: o CRM não tem página de jogador
 * (`/jogador/:id` só existe em apps/public). Passando o resolver aqui, o nome
 * do atleta viraria link para o catch-all `path="*"` — 404 — e no bracket
 * ainda roubaria o clique do card, que precisa abrir o placar da partida.
 *
 * `liveMatchHref`, ao contrário, É passado: o CRM tem a página do placar, só
 * que em outro endereço (`/torneios/:tournamentId/partidas/:matchId`). Sem o
 * torneio não há rota possível, e devolver `null` faz o botão "Ao vivo"
 * desaparecer em vez de navegar para lugar nenhum.
 */
function TournamentsProviderBridge({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <TournamentsProvider
      client={supabase}
      userId={user?.id ?? null}
      liveMatchHref={liveMatchHref}
    >
      {children}
    </TournamentsProvider>
  );
}

/**
 * MutationsBridge — adapter entre `@pps/mutations` (toast agnostico) e o
 * radix toast usado no CRM via `@pps/ui/hooks/use-toast`. M-5 (CF-8).
 * Renderizado dentro de QueryClientProvider + Toaster (ambos requeridos).
 */
function MutationsBridge({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  return (
    <MutationsProvider
      toast={({ title, description, variant }) =>
        toast({
          title,
          description,
          variant: variant === 'error' ? 'destructive' : 'default',
        })
      }
    >
      {children}
    </MutationsProvider>
  );
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <PermissionsProvider>
            <ProfessorScopeProvider>
            <TournamentsProviderBridge>
            <MutationsBridge>
            <ErrorBoundary>
              <SSOHandler />
              <ImpersonationBannerWrapper />
              <ActivityHeartbeat />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
              <Routes>
                <Route path="/sso" element={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>} />
                <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
                {/* Destino do link de recuperacao de senha. Deliberadamente FORA
                    de <PublicRoute>: o clique valido no link cria sessao, e
                    PublicRoute manda quem tem sessao para "/" — o usuario cairia
                    no dashboard sem nunca definir a senha. Mesmo arranjo do app
                    publico (apps/public/src/App.tsx). */}
                <Route path="/reset-password" element={<RouteErrorBoundary><ResetPassword /></RouteErrorBoundary>} />
                <Route path="/onboarding/professor" element={<ProtectedRouteFullScreen><RouteErrorBoundary><ProfessorOnboarding /></RouteErrorBoundary></ProtectedRouteFullScreen>} />
                <Route path="/professor/cadastros" element={<ProtectedRoute><RouteErrorBoundary><ProfessorCadastros /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/professor/financeiro-alunos" element={<ProtectedRoute><RouteErrorBoundary><ProfessorFinanceiroAlunos /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/professor/equipe" element={<ProtectedRoute><RouteErrorBoundary><ProfessorTeam /></RouteErrorBoundary></ProtectedRoute>} />
                {/* Dashboard NÃO é embrulhada em RequirePermission: a própria
                    página já resolve seu branching por staffRole/kind
                    (isProfessorGuest, isTraditionalTeacherStaff) — duplicar
                    a checagem no guard quebraria esse redirect interno. */}
                <Route path="/" element={<ProtectedRoute><RouteErrorBoundary><Dashboard /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/leads" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="leads.view" module="leads"><Leads /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/pipeline" element={<Navigate to="/leads" replace />} />
                <Route path="/oportunidades" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="opportunities.view" module="opportunities"><PlanGate requiredPlan="enterprise"><Opportunities /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/agenda" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="agenda.view" module="agenda"><Agenda /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/membros" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="members.view" module="members"><Members /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/experimentais" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="trials.view" module="academy_trials"><PlanGate requiredPlan="enterprise"><TrialLessons /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/agregadores" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="integrations.view" module="integrations"><PlanGate requiredPlan="enterprise"><Aggregators /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/alunos" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="students.view" module="academy_students"><PlanGate requiredPlan="enterprise"><Students /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/quadras" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="courts.view" module="courts"><Courts /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/professores" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="teachers.view" module="academy_teachers"><PlanGate requiredPlan="enterprise"><Teachers /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/aulas" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="lessons.view" module="academy_lessons"><PlanGate requiredPlan="enterprise"><Lessons /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/reposicoes" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="makeups.view" module="academy_makeups"><PlanGate requiredPlan="enterprise"><Makeup /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/financeiro" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="finance_panel.view" module="finance_panel"><PlanGate requiredPlan="enterprise"><FinancialDashboard /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/planos" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="tuition_plans.view" module="tuition_plans"><PlanGate requiredPlan="enterprise"><Tuition /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/mensalidades" element={<Navigate to="/planos" replace />} />
                <Route path="/faturas" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="invoices.view" module="invoices"><PlanGate requiredPlan="enterprise"><Invoices /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/comissoes" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="commissions.view" module="commissions"><PlanGate requiredPlan="enterprise"><TeacherCommissions /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/pagamentos" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="payment_methods.view" module="payment_methods"><PlanGate requiredPlan="enterprise"><PaymentMethods /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/despesas" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="expenses.view" module="expenses"><PlanGate requiredPlan="enterprise"><Expenses /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/produtos" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="products.view" module="pdv"><PlanGate requiredPlan="enterprise"><Products /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/pdv" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="pdv_sales.view" module="pdv"><PlanGate requiredPlan="enterprise"><Pdv /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/pdv/relatorios" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="pdv_sales.view" module="pdv"><PlanGate requiredPlan="enterprise"><PdvReports /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/notas-fiscais" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="nfse.view" module="nfse"><PlanGate requiredPlan="enterprise"><Nfse /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/torneios" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="tournaments.view" module="tournaments"><Tournaments /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/torneios/:id" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="tournaments.view" module="tournaments"><TournamentDetail /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/torneios/:tournamentId/partidas/:matchId" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="tournaments.view" module="tournaments"><TournamentMatchDetail /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/torneios/:tournamentId/tv" element={<ProtectedRouteFullScreen><RouteErrorBoundary><TournamentTV /></RouteErrorBoundary></ProtectedRouteFullScreen>} />
                <Route path="/categorias" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="categories.view" module="categories"><Categories /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/atendimento" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="support.view" module="support"><Atendimento /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/comunicados" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="announcements.view" module="announcements"><Announcements /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/equipe" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="staff.view" module="staff"><Staff /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/whatsapp" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="integrations.view" module="integrations"><PlanGate requiredPlan="enterprise"><WhatsApp /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/instagram" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="integrations.view" module="integrations"><PlanGate requiredPlan="enterprise"><Instagram /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/api-docs" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="api_docs.view" module="api_docs"><PlanGate requiredPlan="enterprise"><ApiDocs /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/minha-conta" element={<ProtectedRoute><RouteErrorBoundary><AccountSettings /></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/configuracoes" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="settings.view" module="settings"><Settings /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/settings/network" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="settings.view" module="settings"><PlanGate requiredPlan="enterprise"><NetworkSettings /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/notificacoes" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="notifications.view" module="notifications"><Notifications /></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/indicadores" element={<ProtectedRoute><RouteErrorBoundary><RequirePermission permission="indicators.view" module="indicators"><PlanGate requiredPlan="enterprise"><OperationalIndicators /></PlanGate></RequirePermission></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/admin/platform-fees" element={<ProtectedRoute><RouteErrorBoundary><AdminCutoverGate><PlatformFeesAdmin /></AdminCutoverGate></RouteErrorBoundary></ProtectedRoute>} />
                <Route path="/site/:slug" element={<ClubPublicSite />} />
                <Route path="/site/:slug/:section" element={<ClubPublicSite />} />
                {/* Link publico de autocadastro do professor autonomo (P1.2) —
                    FORA de ProtectedRoute, mesmo padrao de /site/:slug. Rota
                    separada da familia /site/:slug (site publico de clube
                    tradicional), que fica intocada. */}
                <Route path="/cadastro/:slug" element={<ProfessorSignupPublic />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
            </MutationsBridge>
            </TournamentsProviderBridge>
            </ProfessorScopeProvider>
            </PermissionsProvider>
          </AuthProvider>
        </BrowserRouter>
        {!isNative && <SpeedInsights />}
        {!isNative && <Analytics />}
        <PwaUpdateWatcher />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
